import { useState } from 'react';
import { toast } from 'sonner';
import {
  useCampaignCapabilitiesStore,
  useCampaignUiStore,
  useShareGrants,
  useMintShareGrant,
  useRevokeShareGrant,
  type MintShareGrantInput,
  type ShareGrantTtl,
} from '@discovr/features';
import { Globe, Lock, MessageCircle, Settings, Share2 } from '../../icons';
import { cn } from '../../lib/utils';
import { titleCaseSlug } from '../../lib/slug';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { ShareButton } from './ShareButton';
import { useCampaignShare } from './useCampaignShare';
import { generateRandomSharePassword } from './defaultSharePassword';
import type { ShortShareInput } from './shortLink';
import { TTL_OPTIONS, TTL_HUMAN, DEFAULT_SHARE_TTL, closestTtlPreset } from './shareGrantTtl';
import { SHARE_DIALOG_CLASS } from './shareDialogChrome';
import { EditShareGrantDialog } from './EditShareGrantDialog';

export interface SharePopoverProps {
  /**
   * Resolved share input for this view — used to find an active grant and
   * build the tail when minting. `null` when no shareable identity resolves
   * (e.g. the admin header's video-panel surface) → renders the plain
   * ShareButton with no grant awareness.
   */
  input: ShortShareInput | null;
  /** Lazily computes the URL to copy at click/copy time (deep-link read fresh). */
  getUrl: () => string | null;
  /** Accessible label + tooltip text for the trigger button. */
  label?: string;
  /** Optional override/extension of the default icon-button styling. */
  className?: string;
  /**
   * Brand mode: hide all public-link controls (make-public toggle, TTL,
   * password, grant machinery) and render just a "Copy link" dialog for the
   * screen's authenticated deep link. Admin leaves this false → full public
   * sharing. The one intended admin/brand divergence; everything else is shared.
   */
  linkOnly?: boolean;
  /**
   * Dialog subtitle in `linkOnly` mode (the current screen name, e.g.
   * "Creators"). Public mode derives its own subtitle from `input`.
   */
  screenName?: string;
}

const ICON_BTN =
  'inline-flex size-9 items-center justify-center rounded-full border border-[#E5E5E5] text-[#737373] transition-colors hover:bg-[#F4F4F2] hover:text-[#171717] disabled:cursor-wait disabled:opacity-50';

const PURPOSE_LABEL: Record<string, string> = {
  script: 'Script',
  content: 'Content',
  insights: 'Insights',
  safety: 'Brand Safety',
  chat: 'Conversation',
};

/** Human label for the dialog subtitle, derived from the share input. */
function describeShareScreen(input: ShortShareInput): string {
  if (input.kind === 'creator') {
    const purpose = PURPOSE_LABEL[input.purpose] ?? titleCaseSlug(input.purpose);
    return `${purpose} · @${input.username}`;
  }
  const tabLabel = titleCaseSlug(input.tab ?? '');
  return input.pill ? `${tabLabel} · ${titleCaseSlug(input.pill)}` : tabLabel;
}

function buildMintPayload(
  input: ShortShareInput,
  tail: string,
  ttl: ShareGrantTtl,
  password?: string,
  allowChatReadonly?: boolean,
): MintShareGrantInput {
  return {
    tail,
    ttl,
    // Always sent explicitly: the current window when input carries one, else
    // ''. The BE 400s on a non-empty window for any other screen.
    window: input.kind === 'page' ? (input.window ?? '') : '',
    ...(password ? { password } : {}),
    // Only the page variant ever carries these — undefined here is dropped by
    // the BE's JSON decode exactly like an omitted key.
    page_size: input.kind === 'page' ? input.pageSize : undefined,
    total_pages: input.kind === 'page' ? input.totalPages : undefined,
    sort_by: input.kind === 'page' ? input.sortBy : undefined,
    sort_dir: input.kind === 'page' ? input.sortDir : undefined,
    // D5: only ever sent as `true` — the BE 400s on this outside a
    // script/content/chat-purpose creator grant, so never send `false`.
    ...(allowChatReadonly ? { allow_chat_readonly: true } : {}),
  };
}

/** Whether the "Allow chat (read-only)" toggle applies — only script/content/
 *  chat-purpose creator grants embed thread messages; the BE 400s otherwise. */
function isChatCapableInput(input: ShortShareInput): boolean {
  return (
    input.kind === 'creator' &&
    (input.purpose === 'script' || input.purpose === 'content' || input.purpose === 'chat')
  );
}

/**
 * Share dialog — public / password-protected / private, with TTL presets.
 * Drop-in replacement for ShareButton at the same icon-button spot.
 *
 * When canShare, the dialog always opens on click: an active grant matching
 * this input shows a Copy/Edit/Invalidate view (Edit opens the shared
 * EditShareGrantDialog); a failed grants query shows an inline error (never the
 * two-toggle state, so an admin can't mint a duplicate over an unseen grant);
 * otherwise a two-toggle "Make public" + "Require password" state. Without
 * canShare it renders the plain ShareButton.
 */
export function SharePopover({ input, getUrl, label = 'Share link', className, linkOnly = false, screenName }: SharePopoverProps) {
  const { canShare, shareInputTail, findActiveGrant } = useCampaignShare();
  const campaignId = useCampaignUiStore((s) => s.campaignId);
  const role = useCampaignCapabilitiesStore((s) => s.role);
  // No brand tier can ever mint a public share grant — enforced here
  // regardless of what the caller passes, so creator-kind shares (Insights/
  // Script/Content/Chat drawers, shared by both apps) don't each need to know
  // the caller's role. Mirrors the BE's buildBrandOwnerScopes (see
  // scopes_map.go): every brand tier holds neither campaign:share:read nor
  // campaign:share:write, so the grants query and mint call would 403 anyway.
  const isBrandRole = role === 'brand_owner' || role === 'brand_admin' || role === 'brand_member';
  const effectiveLinkOnly = linkOnly || isBrandRole;
  // Off in link-only mode, otherwise driven by `canShare` (so the grants
  // query never fetches when off).
  const publicEnabled = canShare && !effectiveLinkOnly;

  // Hooks must run unconditionally regardless of the fallback/link-only branch
  // below — `publicEnabled` just controls whether the query actually fetches.
  const grantsQuery = useShareGrants(campaignId, { enabled: publicEnabled });
  const mintGrant = useMintShareGrant(campaignId);
  const revokeGrant = useRevokeShareGrant(campaignId);

  const [open, setOpen] = useState(false);
  // Defaults ON: the dialog exists to create a shareable link, so the
  // common case shouldn't need a toggle first. Exposure is bounded by
  // DEFAULT_SHARE_TTL (24h) rather than by making the admin opt in.
  const [makePublic, setMakePublic] = useState(true);
  const [requirePassword, setRequirePassword] = useState(false);
  const [ttl, setTtl] = useState<ShareGrantTtl>(DEFAULT_SHARE_TTL);
  const [password, setPassword] = useState('');
  // D5: "Allow chat (read-only)" toggle — defaults OFF (opt-in), unlike
  // Make public. Only ever rendered/relevant for a script/content/chat-
  // purpose creator grant — see isChatCapableInput.
  const [allowChatReadonly, setAllowChatReadonly] = useState(false);
  // Gear icon in the active-grant state opens the shared EditShareGrantDialog
  // (TTL + password edits) rather than a separate remint flow here.
  const [editOpen, setEditOpen] = useState(false);

  // Tracks the dialog's previous open value so the reset-on-open logic below
  // can adjust-during-render. Declared unconditionally with the other hooks so
  // it runs every render regardless of the early return below.
  const [prevOpen, setPrevOpen] = useState(open);

  // Resolves true only when the link actually reached the clipboard, so callers
  // can keep the dialog open on failure (the user still needs the visible link).
  const copyUrl = async (): Promise<boolean> => {
    const url = getUrl();
    if (!url) {
      toast.error('Could not create link');
      return false;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
      return true;
    } catch {
      toast.error('Copy failed — check clipboard permissions');
      return false;
    }
  };

  // Brand link-only mode: a plain "Copy link" dialog for the authenticated
  // deep link — no public toggle/TTL/password/grant states.
  if (effectiveLinkOnly) {
    // The page-kind header share (BrandLayout) always passes an explicit
    // screenName with no `input`. A creator-kind share (Insights/Script/
    // Content/Chat drawers) forced into link-only mode by isBrandRole above
    // carries `input` instead — fall back to the same description the public
    // two-toggle dialog would have shown, so brand doesn't lose the "Insights
    // · @handle" subtitle just because it can't mint a public grant.
    const name = screenName ?? (input ? describeShareScreen(input) : undefined);
    const previewUrl = open ? getUrl() : null;
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <button type="button" aria-label={label} title={label} className={cn(ICON_BTN, className)}>
            <Share2 size={16} />
          </button>
        </DialogTrigger>
        <DialogContent className={SHARE_DIALOG_CLASS} showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Share this screen</DialogTitle>
            <DialogDescription>{name ?? 'Copy a link to this screen'}</DialogDescription>
          </DialogHeader>
          {previewUrl ? (
            <div className="flex flex-col gap-1.5">
              <span className="text-body-sm-medium text-foreground">Private Link:</span>
              <div
                aria-label="Link preview"
                className="rounded-lg bg-muted px-3 py-2 text-caption-regular break-all text-muted-foreground select-all"
              >
                {previewUrl}
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void copyUrl().then((copied) => { if (copied) setOpen(false); })}>
              Copy link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Flag off, no admin-share capability, or no resolvable descriptor for this
  // surface (e.g. the video-panel edge case) — behave exactly like the plain
  // button that existed before this feature.
  if (!publicEnabled || !input) {
    return <ShareButton getUrl={getUrl} label={label} className={className} />;
  }

  const activeGrant =
    !grantsQuery.isLoading && !grantsQuery.isError && grantsQuery.data
      ? findActiveGrant(grantsQuery.data, input)
      : undefined;

  // Reset fields on each closed -> open transition so a previous session's
  // picks don't bleed into the next share. Password always regenerates fresh.
  // allowChatReadonly is seeded from the live grant (not false) because the BE
  // writes it unconditionally on every mint, so the toggle must reflect what's
  // actually live.
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setMakePublic(true);
      setRequirePassword(false);
      setTtl(DEFAULT_SHARE_TTL);
      setPassword(generateRandomSharePassword());
      setAllowChatReadonly(activeGrant?.allow_chat_readonly ?? false);
      setEditOpen(false);
    }
  }

  const handleMakePublicChange = (checked: boolean) => {
    setMakePublic(checked);
    // "Require password" is hidden entirely while Make public is off (its
    // value is meaningless there, since Copy link skips the mint entirely in
    // that case) — reset it so it doesn't silently reappear pre-checked.
    if (!checked) setRequirePassword(false);
  };

  const handleTtlChange = (next: ShareGrantTtl) => {
    // Re-clicking the preset that is already selected is a no-op, so it must
    // not re-announce itself — the segmented control already shows which one
    // is active, and a toast per click stacks up saying nothing new.
    if (next === ttl) return;
    setTtl(next);
    // No position override: every other toast in both apps uses the app-wide
    // <Toaster position="bottom-right">, and this was the only caller opting
    // out of it.
    toast(`Expiry set to ${TTL_HUMAN[next]}`);
  };

  const handleCopy = async (): Promise<void> => {
    if (makePublic) {
      const tail = shareInputTail(input);
      try {
        await mintGrant.mutateAsync(
          buildMintPayload(input, tail, ttl, requirePassword ? password : undefined, allowChatReadonly),
        );
      } catch {
        // useMintShareGrant already surfaced an error toast — stop here so a
        // failed mint never copies a link to a grant that doesn't exist, and
        // leave the dialog open so the admin can retry.
        return;
      }
    }
    // Close only when the link reached the clipboard; on a copy failure keep
    // the dialog open so the admin still has the link (and can retry).
    if (await copyUrl()) setOpen(false);
  };

  const handleInvalidate = () => {
    if (!activeGrant) return;
    // No confirm needed — the grant is re-mintable. Once revoke resolves,
    // `activeGrant` stops matching and the two-toggle state reappears.
    revokeGrant.mutate(activeGrant.id);
    // Reset explicitly: allowChatReadonly was seeded from this grant on open,
    // and the open/close reset won't fire on this same-open transition, so
    // without it the toggle carries the revoked grant's chat setting forward.
    setAllowChatReadonly(false);
  };

  const statusLine = makePublic ? `View only · expires ${TTL_HUMAN[ttl]}` : 'Private link';

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <button
            type="button"
            aria-label={label}
            title={label}
            disabled={grantsQuery.isLoading}
            className={cn(ICON_BTN, className)}
          >
            <Share2 size={16} />
          </button>
        </DialogTrigger>
        <DialogContent className={SHARE_DIALOG_CLASS} showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Share this screen</DialogTitle>
            <DialogDescription>{describeShareScreen(input)}</DialogDescription>
          </DialogHeader>

          {activeGrant ? (
            <div className="flex flex-col gap-4">
              <p className="flex items-center gap-2 text-body-sm-regular text-foreground">
                A public link is already active for this screen
                {activeGrant.password_protected && (
                  <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-caption-medium text-muted-foreground">
                    Password protected
                  </span>
                )}
              </p>
              <p className="text-caption-regular text-muted-foreground">
                View only · expires {TTL_HUMAN[closestTtlPreset(activeGrant)]}
              </p>
              <DialogFooter className="sm:justify-start">
                <Button type="button" variant="outline" onClick={() => void copyUrl()}>
                  Copy
                </Button>
                <Button type="button" variant="outline" onClick={() => setEditOpen(true)}>
                  <Settings size={14} />
                  Edit
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleInvalidate}
                  disabled={revokeGrant.isPending}
                >
                  Invalidate
                </Button>
              </DialogFooter>
            </div>
          ) : grantsQuery.isError ? (
            // A failed grants lookup must not fall through to the two-toggle
            // state — that would let an admin mint a duplicate over a grant
            // they couldn't see.
            <div className="flex flex-col gap-4">
              <p className="text-body-sm-regular text-rose-500">
                {grantsQuery.error instanceof Error ? grantsQuery.error.message : 'Failed to load shared links'}
              </p>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Close
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-start gap-2.5">
                  <Globe size={16} className="mt-0.5 shrink-0 text-muted-foreground" aria-hidden />
                  <span className="flex flex-col">
                    <span className="text-body-sm-medium text-foreground">Make public</span>
                    <span className="text-caption-regular text-muted-foreground">
                      Anyone with link can view
                    </span>
                  </span>
                </div>
                <Switch
                  checked={makePublic}
                  onCheckedChange={handleMakePublicChange}
                  aria-label="Make public"
                />
              </div>

              {makePublic && (
                <div
                  role="group"
                  aria-label="Link expiry"
                  className="flex items-center rounded-full bg-[#F4F4F2] p-1"
                >
                  {TTL_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => handleTtlChange(opt.value)}
                      aria-pressed={ttl === opt.value}
                      className={cn(
                        'flex-1 rounded-full px-3 py-1.5 text-body-sm-medium leading-none transition-colors',
                        ttl === opt.value
                          ? 'bg-white text-[#171717] shadow-[0px_2px_6px_-1px_rgba(0,0,0,0.04),0px_1px_2px_rgba(0,0,0,0.04)]'
                          : 'text-[#737373] hover:text-[#171717]',
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}

              {makePublic && (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-start gap-2.5">
                    <Lock size={16} className="mt-0.5 shrink-0 text-muted-foreground" aria-hidden />
                    <span className="flex flex-col">
                      <span className="text-body-sm-medium text-foreground">Require password</span>
                      <span className="text-caption-regular text-muted-foreground">
                        {requirePassword ? 'Password set' : 'No password set'}
                      </span>
                    </span>
                  </div>
                  <Switch
                    checked={requirePassword}
                    onCheckedChange={setRequirePassword}
                    aria-label="Require password"
                  />
                </div>
              )}

              {makePublic && requirePassword && (
                <div className="flex items-center gap-2">
                  <Input
                    aria-label="Share password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setPassword(generateRandomSharePassword())}
                  >
                    Generate
                  </Button>
                </div>
              )}

              {makePublic && isChatCapableInput(input) && (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-start gap-2.5">
                    <MessageCircle size={16} className="mt-0.5 shrink-0 text-muted-foreground" aria-hidden />
                    <span className="flex flex-col">
                      <span className="text-body-sm-medium text-foreground">Allow chat (read-only)</span>
                      <span className="text-caption-regular text-muted-foreground">
                        Visitors can read the conversation, never post
                      </span>
                    </span>
                  </div>
                  <Switch
                    checked={allowChatReadonly}
                    onCheckedChange={setAllowChatReadonly}
                    aria-label="Allow chat (read-only)"
                  />
                </div>
              )}

              <p className="text-caption-regular text-muted-foreground">{statusLine}</p>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="button" onClick={() => void handleCopy()}>
                  Copy link
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Separate Dialog stacked on top of the share dialog. On a successful
          edit, useUpdateShareGrant invalidates the same grants query this
          component reads, so `activeGrant` above reflects the edit. */}
      {activeGrant && (
        <EditShareGrantDialog grant={activeGrant} open={editOpen} onOpenChange={setEditOpen} />
      )}
    </>
  );
}
